import React, { useCallback, useEffect, useState } from "react"
import PropTypes from "prop-types"
import Cropper from "react-easy-crop"
import Slider from "@material-ui/core/Slider"
import Dialog from "@material-ui/core/Dialog"
import DialogContent from "@material-ui/core/DialogContent"
import DialogActions from "@material-ui/core/DialogActions"
import DialogTitle from "@material-ui/core/DialogTitle"
import DefaultButton from "../DefaultButton"
import toastError from "../../errors/toastError"

const criarImagem = (cUrl) =>
  new Promise((resolve, reject) => {
    const oImagem = new Image()
    oImagem.addEventListener("load", () => resolve(oImagem))
    oImagem.addEventListener("error", (error) => reject(error))
    oImagem.src = cUrl
  })

export async function obterImagemCortada(cOrigemImagem, oCortePixels, nProporcao, cMimeSaida, nLarguraSaida) {
  const oImagem = await criarImagem(cOrigemImagem)
  const nLarguraBase = nProporcao >= 1 ? 1280 : 720
  const nLargura = nLarguraSaida > 0
    ? nLarguraSaida
    : nProporcao >= 1 ? nLarguraBase : Math.round(nLarguraBase * nProporcao)
  const nAltura = nLarguraSaida > 0
    ? Math.round(nLarguraSaida / nProporcao)
    : nProporcao >= 1 ? Math.round(nLarguraBase / nProporcao) : nLarguraBase
  const oCanvas = document.createElement("canvas")
  oCanvas.width = nLargura
  oCanvas.height = nAltura
  const oContexto = oCanvas.getContext("2d")

  oContexto.drawImage(
    oImagem,
    oCortePixels.x,
    oCortePixels.y,
    oCortePixels.width,
    oCortePixels.height,
    0,
    0,
    nLargura,
    nAltura
  )

  return new Promise((resolve) => {
    oCanvas.toBlob((oBlob) => resolve(oBlob), cMimeSaida)
  })
}

const ImageCropper = ({
  lAberto,
  aoFechar,
  cOrigemImagem,
  aoConfirmar,
  nProporcao = 1,
  cMimeSaida = "image/png",
  cNomeArquivo = "imagem.png",
  cTitulo = "Ajustar imagem",
  nLarguraSaida,
}) => {
  const [oCorte, setOCorte] = useState({ x: 0, y: 0 })
  const [nZoom, setNZoom] = useState(1)
  const [oAreaCortadaPixels, setOAreaCortadaPixels] = useState(null)

  useEffect(() => {
    setOCorte({ x: 0, y: 0 })
    setNZoom(1)
    setOAreaCortadaPixels(null)
  }, [cOrigemImagem, nProporcao])

  const aoCompletarCorte = useCallback((oAreaCortada, oAreaCortadaPixelsParam) => {
    setOAreaCortadaPixels(oAreaCortadaPixelsParam)
  }, [])

  const lidarComConfirmacao = async () => {
    if (!oAreaCortadaPixels) {
      toastError("Aguarde a imagem carregar antes de aplicar.")
      return
    }

    try {
      const oBlob = await obterImagemCortada(cOrigemImagem, oAreaCortadaPixels, nProporcao, cMimeSaida, nLarguraSaida)
      if (!oBlob) {
        toastError("Não foi possível gerar a imagem. Tente outro arquivo.")
        return
      }
      const oArquivo = new File([oBlob], cNomeArquivo, { type: cMimeSaida })
      await aoConfirmar(oArquivo)
    } catch (oErro) {
      toastError(oErro, "Não foi possível preparar a imagem.")
    }
  }

  if (!cOrigemImagem) return null

  return (
    <Dialog open={lAberto} onClose={aoFechar} fullWidth maxWidth={nProporcao > 1 ? "md" : "sm"} aria-labelledby="dialog-cropper-title">
      <DialogTitle id="dialog-cropper-title">{cTitulo}</DialogTitle>
      <DialogContent>
        <div style={{ position: "relative", width: "100%", height: nProporcao > 1 ? 360 : 420, background: "#333" }}>
          <Cropper
            image={cOrigemImagem}
            crop={oCorte}
            zoom={nZoom}
            aspect={nProporcao}
            showGrid={false}
            cropShape="rect"
            onCropChange={setOCorte}
            onZoomChange={setNZoom}
            onCropComplete={aoCompletarCorte}
          />
        </div>
        <div style={{ marginTop: 16 }}>
          <Slider value={nZoom} min={1} max={3} step={0.1} onChange={(e, v) => setNZoom(v)} aria-labelledby="Zoom" />
        </div>
      </DialogContent>
      <DialogActions>
        <DefaultButton stylization="secondary" onClick={aoFechar}>
          Cancelar
        </DefaultButton>
        <DefaultButton stylization="primary" onClick={lidarComConfirmacao}>
          Aplicar
        </DefaultButton>
      </DialogActions>
    </Dialog>
  )
}

ImageCropper.propTypes = {
  lAberto: PropTypes.bool.isRequired,
  aoFechar: PropTypes.func.isRequired,
  cOrigemImagem: PropTypes.string,
  aoConfirmar: PropTypes.func.isRequired,
  nProporcao: PropTypes.number,
  cMimeSaida: PropTypes.string,
  cNomeArquivo: PropTypes.string,
  cTitulo: PropTypes.string,
  nLarguraSaida: PropTypes.number,
}

export default ImageCropper
